import { useRef } from 'react';
import type { ReactNode } from 'react';
import { useFocusTrap } from '../lib/useFocusTrap';

type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

interface ModalProps {
  /** Close handler — fired by Escape, backdrop click, and (if used) the close button. */
  onClose: () => void;
  /** Accessible name for the dialog (usually the visible title text). Required
   * so the dialog is announced (WCAG 4.1.2 / aria-modal naming). */
  ariaLabel: string;
  role?: 'dialog' | 'alertdialog';
  /** Centered max width. Optional — omit and pass an exact `max-w-*` in
   * panelClassName to preserve a width not in the scale. Ignored for drawer. */
  size?: Size;
  variant?: 'center' | 'drawer';
  /** Extra classes appended to the panel (per-modal padding / spacing / etc.). */
  panelClassName?: string;
  children: ReactNode;
}

const SIZE: Record<Size, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-5xl',
  // Drill-down modals (JCD-59): wide enough for a detail table without
  // becoming a full-screen takeover on a desktop monitor.
  '4xl': 'max-w-6xl',
};

/**
 * Accessible dialog shell. Renders a scrim + a focus-trapped panel and supplies
 * role/aria-modal/aria-label, Escape-to-close, backdrop-click-to-close, focus
 * restoration, and body scroll lock (all via useFocusTrap) — the behaviors the
 * ~14 hand-rolled `fixed inset-0` overlays each lacked. Deliberately minimal:
 * migrated modals keep their own header/content/footer and just wrap them here,
 * dropping their bespoke overlay div + manual keydown effect.
 *
 * Render conditionally from the parent (mount = open), matching the existing
 * pattern — no portal needed at this scale.
 */
export default function Modal({
  onClose,
  ariaLabel,
  role = 'dialog',
  size,
  variant = 'center',
  panelClassName = '',
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, onClose);
  const isDrawer = variant === 'drawer';

  return (
    <div
      className={
        'fixed inset-0 z-modal bg-black/60 flex p-4 ' +
        (isDrawer ? 'justify-end' : 'items-center justify-center')
      }
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={
          'bg-tremor-background shadow-xl outline-none ' +
          (isDrawer
            ? 'h-full w-full max-w-md rounded-l-tremor-default overflow-y-auto '
            : `rounded-tremor-default w-full max-h-[90vh] overflow-y-auto ${size ? SIZE[size] + ' ' : ''}`) +
          panelClassName
        }
      >
        {children}
      </div>
    </div>
  );
}
